import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  Typography,
  IconButton,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  SxProps,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PieChartOutlineOutlinedIcon from "@mui/icons-material/PieChartOutlineOutlined";
import { GETDASHBOARDTAGDATA } from "../api/endpoints";
import { baseUrl } from "../utils/constants";
import { client } from "../api/client";
import {
  getChartData,
  getEventsOverview,
  getMandatoryMessageOverAllStats,
  getRecentEventsAndGroups,
} from "../api/requests/dashboard";
import DashboardChart from "./DashboardChart";
import { getTagResponsesByEventMsgId } from "../api/requests/events";
import EventTagPieChart from "./EventTagPieChart";
import DatesDropdown from "./DatesDropdown";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { loaderSx } from "./ViewRecipientsDialog";
import { convertISTtoUTC, getFromNow } from "../utils/utils";
import {
  ALLMESSAGESPATH,
  EVENTPATH,
  GROUPPATH,
  USERSPATH,
} from "../routes/routePaths";

//Images
import eventsIcon from "../../src/assets/Events.svg";
import groupsIcon from "../../src/assets/Groups.svg";
import usersIcon from "../../src/assets/Users.svg";
import googleMessagesIcon from "../../src/assets/GoogleMessages.svg";
import featureSearchIcon from "../../src/assets/FeatureSearch.svg";
import featuredPlaylistIcon from "../../src/assets/FeaturedPlaylist.svg";
import textAdRoundedIcon from "../../src/assets/TextAdRounded.svg";
import mandatoryMessagesBackground from "../../src/assets/MandatoryMessagesBackground.svg";
import AiSummaryRepoBackground from "../../src/assets/AiSummaryRepoBackground.svg";
import eventMessagesBackground from "../../src/assets/EventMessagesBackground.svg";
import aiSummaryReportImage from "../../src/assets/AiSummaryReportImage.svg";
import { titleStyles } from "../components/CommDataGrid";

export interface DateRangeType {
  fromDate: string;
  toDate: string;
}

type AnalyticsCardProps = {
  title: string;
  description: string;
  icon?: string;
  path: string;
  backgroundImage: string;
  statsData: { [key: string]: number | string };
};

interface DashboardItem {
  title: string;
  value: number;
  icon: React.ReactNode;
  iconColor: string;
  path?: string;
  filter?: string;
}
interface RecentEvent {
  id: number;
  name: string;
  count: number;
  createdOn: string;
}

interface ChartDataItem {
  eventsCount: number;
  messagesCount: number;
  month: string;
  usersUsingCount: number;
}

interface PieChartDataItemType {
  count: number;
  eventTagId: number;
  tagColour: string;
  tagName: string;
}

interface DashboardData {
  numberOfUsers: number;
  numberOfGroups: number;
  numberOfEvents: number;
  numberOfTextMessages: number;
  numberOfMediaMessages: number;
  numberOfTranslations: number;
  numberOfDraftings: number;
  numberOfActiveUsers: number;
}

const AnalyticsCard = ({
  title,
  description,
  path,
  backgroundImage,
  statsData,
}: AnalyticsCardProps) => {
  const navigate = useNavigate();

  return (
    <Box onClick={() => navigate(path)} sx={analyticsCardSx(backgroundImage)}>
      <Box
        sx={{ display: "flex", flexDirection: "column", gap: 2, height: 90 }}
      >
        <Typography sx={analyticCardTitleSx}>{title}</Typography>
        <Typography
          sx={{ color: "#FBFBFB", fontSize: "14px", fontWeight: 400 }}
        >
          {description}
        </Typography>
      </Box>
      <Box sx={statsBoxStyles}>
        {Object.entries(statsData).map(([label, value]) => (
          <Box key={label} sx={subCardStyles}>
            <Typography fontSize={14} color="#fff">
              {String(value)}
            </Typography>
            <Typography fontSize={12} color="#fff">
              {label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [tabIndex, setTabIndex] = useState<number>(0);
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    numberOfUsers: 0,
    numberOfGroups: 0,
    numberOfEvents: 0,
    numberOfTextMessages: 0,
    numberOfMediaMessages: 0,
    numberOfTranslations: 0,
    numberOfDraftings: 0,
    numberOfActiveUsers: 0,
  });
  const [recentEvents, setRecentEvents] = useState<RecentEvent[]>([]);
  const [recentGroups, setRecentGroups] = useState<RecentEvent[]>([]);
  const [chartData, setChartData] = useState<ChartDataItem[]>([]);
  const [openPieChart, setOpenPieChart] = useState<boolean>(false);
  const [pieChartData, setPieChartData] = useState<PieChartDataItemType[]>([]);
  // Initialize default dates for "This Month" in UTC
  const [selectedDates, setSelectedDates] = useState<DateRangeType>({
    fromDate: convertISTtoUTC(dayjs().startOf("month").startOf("day")),
    toDate: convertISTtoUTC(dayjs().endOf("month").endOf("day")),
  });
  const [mandatoryMessagesStats, setMandatoryMessagesStats] = useState({
    "Total messages": 0,
    "Total recipients": 0,
    "Seen percentage": 0,
  });
  const [eventStats, setEventStats] = useState({
    "Total events": 0,
    "Total event messages": 0,
  });
  const [aiSummaryStats, setAiSummaryStats] = useState({
    "All messages": 0,
  });

  const cardData: AnalyticsCardProps[] = [
    {
      title: "Mandatory Messages",
      description: "View mandatory messages statistics and recipient details.",
      path: "/mandatorymessagedashboard",
      backgroundImage: mandatoryMessagesBackground,
      statsData: mandatoryMessagesStats,
    },
    {
      title: "Event Messages",
      description: "Track and analyze event-related messages.",
      path: "/eventmessagedashboard",
      backgroundImage: eventMessagesBackground,
      statsData: eventStats,
    },
    {
      title: "AI Summary Report",
      description: "Track and analyze AI Summary Report messages.",
      icon: aiSummaryReportImage,
      path: "/aisummaryreport",
      backgroundImage: AiSummaryRepoBackground,
      statsData: aiSummaryStats,
    },
  ];

  const fetchRecentEventsData = async () => {
    try {
      const response = await getRecentEventsAndGroups();
      setRecentEvents(response.data.recentEvents);
      setRecentGroups(response.data.recentGroups);
    } catch (error) {
      console.error("Error fetching recent events and groups:", error);
    }
  };

  const fetchChartData = async () => {
    setLoading(true);
    try {
      const response = await getChartData({
        fromDate: selectedDates.fromDate,
        toDate: selectedDates.toDate,
      });
      if (response.status === 200) {
        setChartData(response.data);
      }
    } catch (error) {
      console.error("Error fetching chart data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEventPieChartData = async (eventId: number) => {
    try {
      const response = await getTagResponsesByEventMsgId({
        eventId,
        messageId: 0,
      });
      if (response.status === 200) {
        setPieChartData(response.data);
      }
    } catch (error) {
      console.error("Error fetching pie chart data:", error);
    }
  };

  const closePieChartDialog = () => {
    setOpenPieChart(false);
    setPieChartData([]);
  };

  const handleDateChange = (fromDate: string, toDate: string) => {
    setSelectedDates({ fromDate, toDate });
  };

  const dashboadCardItems: DashboardItem[] = [
    {
      title: "Total users",
      value: dashboardData.numberOfUsers,
      icon: <img src={usersIcon} alt="Users" />,
      path: USERSPATH,
      iconColor: "#E5322D",
    },
    {
      title: "Total active users",
      value: dashboardData.numberOfActiveUsers,
      icon: <img src={usersIcon} alt="Users" />,
      iconColor: "#E5322D",
    },
    {
      title: "Total groups",
      value: dashboardData.numberOfGroups,
      icon: <img src={groupsIcon} alt="Groups" />,
      path: GROUPPATH,
      iconColor: "#CDA767",
    },
    {
      title: "Total events",
      value: dashboardData.numberOfEvents,
      icon: <img src={eventsIcon} alt="Events" />,
      path: EVENTPATH,
      iconColor: "#2031A2",
    },
    {
      title: "Text messages",
      value: dashboardData.numberOfTextMessages,
      icon: <img src={textAdRoundedIcon} alt="Text Messages" />,
      path: ALLMESSAGESPATH,
      filter: "Text",
      iconColor: "#2031A2",
    },
    {
      title: "Multimedia messages",
      value: dashboardData.numberOfMediaMessages,
      icon: <img src={googleMessagesIcon} alt="Multimedia Messages" />,
      path: ALLMESSAGESPATH,
      filter: "Multimedia",
      iconColor: "#2031A2",
    },
    {
      title: "Translate feature usage",
      value: dashboardData.numberOfTranslations,
      icon: <img src={featuredPlaylistIcon} alt="Translate Feature Usage" />,
      iconColor: "#E5322D",
    },

    {
      title: "Drafting feature usage",
      value: dashboardData.numberOfDraftings,
      icon: <img src={featureSearchIcon} alt="Drafting Feature Usage" />,
      iconColor: "#E5322D",
    },
  ];

  const getOverallStatistics = async () => {
    const response = await getMandatoryMessageOverAllStats();
    console.log("mandatoyMes", response?.data);
    if (response && response.data) {
      cardData[0].statsData = response.data;
    }
    setMandatoryMessagesStats({
      "Total messages": response.data.totalMessages || 0,
      "Total recipients": response.data.totalRecipients || 0,
      "Seen percentage": response.data.seenPercentage || 0,
    });
  };

  const getEventsOverviewData = async () => {
    const response = await getEventsOverview();
    setEventStats({
      "Total events": response.data.totalEvents || 0,
      "Total event messages": response.data.totalEventMessages || 0,
    });
  };

  // Fetch the dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const response = await client.get(
          `${baseUrl}${GETDASHBOARDTAGDATA}?fromDate=${selectedDates.fromDate}&toDate=${selectedDates.toDate}`
        );
        if (response && response.data) setDashboardData(response.data);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      }
    };

    fetchDashboardData();
    fetchChartData();
  }, [selectedDates]);

  useEffect(() => {
    fetchRecentEventsData();
    getOverallStatistics();
    getEventsOverviewData();
  }, []);

  return (
    <>
      <Box sx={mainContentStyles}>
        {/* Title Section */}
        <Box sx={titleSectionStyles}>
          <Box>
            <Typography sx={titleStyles}>Dashboard</Typography>
          </Box>
          <DatesDropdown onDateChange={handleDateChange} />
        </Box>
      </Box>

      {/* Scrollable Content */}
      <Box sx={scrollBarWrapperSx}>
        {/* Stats Cards Grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))",
            gap: 2,
          }}
        >
          {dashboadCardItems.map((item, index) => (
            <Card
              key={index}
              sx={statsCardSx(item.path)}
              onClick={() =>
                item.path &&
                navigate(item.path, {
                  state: { from: "dashboard", filter: item?.filter || null },
                })
              }
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <IconButton sx={statsIconSx(item.iconColor)}>
                  {item.icon}
                </IconButton>
                <Box sx={{ ml: 3, display: "flex", flexDirection: "column" }}>
                  <Typography
                    sx={{ color: "#000", fontSize: "20px", fontWeight: 600 }}
                  >
                    {item.value || 0}
                  </Typography>
                  <Typography
                    sx={{ color: "#9E9E9E", fontSize: "16px", fontWeight: 400 }}
                  >
                    {item.title}
                  </Typography>
                </Box>
              </Box>
            </Card>
          ))}
        </Box>
        {/* Usage Analytics */}
        <Card sx={{ ...usageAnalyticsCardSx }}>
          <Typography
            sx={{ color: "#343434", fontWeight: 400, fontSize: "18px", mb: 2 }}
          >
            Usage Analytics
          </Typography>
          <Box>
            {loading ? (
              <Box sx={{ ...loaderSx, height: "100%" }}>
                <CircularProgress size={60} thickness={6} />
              </Box>
            ) : chartData && chartData.length > 0 ? (
              <DashboardChart chartData={chartData} />
            ) : (
              <Box sx={{ ...loaderSx, height: "100%" }}>
                <Typography variant="body2" color="textSecondary">
                  No data found.
                </Typography>
              </Box>
            )}
          </Box>
        </Card>
        {/* Stats Cards */}
        <Box
          sx={{
            ...cardsWrapperSx,
            p: 3,
            backgroundColor: "#ffffff",
            borderRadius: "8px",
          }}
        >
          {cardData &&
            cardData.map((card, index) => (
              <AnalyticsCard
                key={index}
                title={card.title}
                description={card.description}
                icon={card.icon}
                path={card.path}
                backgroundImage={card.backgroundImage}
                statsData={card.statsData}
              />
            ))}
        </Box>
        {/* Recent Events and Recent Groups Tabs */}
        <Box>
          <Tabs
            value={tabIndex}
            onChange={(e, newValue) => setTabIndex(newValue)}
            sx={tabsStyles}
          >
            <Tab label="Recent Events" sx={tabStyles} />
            <Tab label="Recent Groups" sx={{ ...tabStyles, ml: 2 }} />
          </Tabs>
          {/* Display content based on the active tab */}
          {tabIndex === 0 && (
            <Box sx={cardsWrapperSx}>
              {recentEvents?.map((event, index) => (
                <Card key={index} sx={cardSx}>
                  <Box sx={recentEventsSx}>
                    <Tooltip title={event.name} placement="top">
                      <Typography sx={recentEventTitleSx}>
                        {event.name}
                      </Typography>
                    </Tooltip>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          color: "#ffffff",
                          fontWeight: 400,
                        }}
                      >
                        {event.count} members
                      </Typography>
                      <Typography sx={daysPeriodSx}>
                        {getFromNow(event.createdOn)}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={pieChartWrapperSx}>
                    <PieChartOutlineOutlinedIcon
                      sx={{
                        fontSize: "30px",
                        color: "#ffffff",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        setOpenPieChart(true);
                        fetchEventPieChartData(event.id);
                      }}
                    />
                  </Box>
                </Card>
              ))}
            </Box>
          )}
          {tabIndex === 1 && (
            <Box sx={cardsWrapperSx}>
              {recentGroups.map((group, index) => (
                <Card key={index} sx={cardSx}>
                  <Box sx={recentEventsSx}>
                    <Tooltip title={group.name} placement="top">
                      <Typography sx={recentEventTitleSx}>
                        {group.name}
                      </Typography>
                    </Tooltip>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          color: "#ffffff",
                          fontWeight: 400,
                        }}
                      >
                        {group.count} members
                      </Typography>
                      <Typography sx={daysPeriodSx}>
                        {getFromNow(group.createdOn)}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              ))}
            </Box>
          )}
        </Box>
      </Box>
      {/* Pie Chart Dialog */}
      <Dialog
        open={openPieChart}
        onClose={closePieChartDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography sx={sectionTitleSx}>View Event Details</Typography>
          <CloseIcon
            sx={{ fontSize: 16, color: "#9e9e9e", cursor: "pointer" }}
            onClick={closePieChartDialog}
          />
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <EventTagPieChart pieChartData={pieChartData} />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Dashboard;

const tabsStyles: SxProps = {
  borderBottom: "none",
  "& .MuiTabs-indicator": {
    display: "none",
  },
  display: "flex",
  mb: 2,
};

const tabStyles: SxProps = {
  width: "175px",
  height: "42px",
  textTransform: "none",
  fontSize: "14px",
  fontWeight: 500,
  color: "#878787",
  backgroundColor: "#ffffff",
  borderRadius: "68px",
  padding: "10px",
  "&:hover": {
    color: "rgb(87, 153, 219)",
    border: "1px solid rgb(140, 153, 219)",
  },
  "&.Mui-selected": {
    color: "#2E3978",
    border: "1px solid #2E3978",
  },
};

const statsBoxStyles: SxProps = {
  display: "flex",
  width: "100%",
  gap: 1,
};

const subCardStyles: SxProps = {
  background: "rgba(255, 255, 255, 0.2)",
  padding: "8px",
  borderRadius: "4px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  whiteSpace: "nowrap",
};

const daysPeriodSx: SxProps = {
  fontSize: "12px",
  color: "#ffffff",
  fontWeight: 400,
  whiteSpace: "nowrap",
};

const cardsWrapperSx: SxProps = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
  gap: 2,
};

const cardSx: SxProps = {
  p: 3,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  background: "#2E3978",
  borderRadius: "12px",
};

const sectionTitleSx: SxProps = {
  color: "#020817",
  fontWeight: 600,
  fontSize: "18px",
};

const recentEventTitleSx: SxProps = {
  maxWidth: "220px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  color: "#ffffff",
  fontSize: "18px",
  fontWeight: 600,
};

const analyticsCardSx: (backgroundImage: string) => SxProps = (
  backgroundImage
) => ({
  background: `url(${backgroundImage})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  padding: 2,
  cursor: "pointer",
  borderRadius: "20px",
  display: "flex",
  flexDirection: "column",
  // gap: 2,
});

const statsCardSx: (path?: string) => SxProps = (path) => ({
  padding: "24px 16px 24px 16px",
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  borderRadius: "8px",
  backgroundColor: "#fff",
  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  ...(path ? { cursor: "pointer" } : {}),
  "&:hover": path ? { boxShadow: "0 4px 8px rgba(0,0,0,0.2)" } : {},
});

const statsIconSx: (iconColor: string) => SxProps = (iconColor) => ({
  background: iconColor || "#2031A2",
  color: "#fff",
  borderRadius: "50%",
  width: "46px",
  height: "46px",
  "&.MuiIconButton-root": {
    cursor: "default",
  },
});

const usageAnalyticsCardSx: SxProps = {
  p: 2,
  backgroundColor: "#ffffff",
  borderRadius: "8px",
  minHeight: "360px",
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

const pieChartWrapperSx: SxProps = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  gap: "10px",
};

const recentEventsSx: SxProps = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "10px",
};

const analyticCardTitleSx: SxProps = {
  color: "#FDFDFD",
  fontSize: "18px",
  fontWeight: 500,
};

export const mainContentStyles: SxProps = {
  position: "sticky",
  top: 0,
  zIndex: 10,
  pt: 1,
  pb: 2,
};

const titleSectionStyles: SxProps = {
  display: "flex",
  justifyContent: "space-between",
  px: 3,
  pt: 1,
};

const scrollBarWrapperSx: SxProps = {
  height: "calc(100vh - 170px)",
  overflow: "auto",
  px: 3,
  display: "flex",
  flexDirection: "column",
  gap: 3,
};
